/**
 * c15t Consent Management Configuration
 * Defines consent categories, storage, and Zaraz integration
 */

export interface ConsentCategory {
  id: string;
  name: string;
  description: string;
  required: boolean;
}

export const consentCategories: ConsentCategory[] = [
  {
    id: 'necessary',
    name: 'Necessary',
    description: 'Required for the website to function properly. Always enabled.',
    required: true,
  },
  {
    id: 'analytics',
    name: 'Analytics',
    description: 'Help us understand how you use our site to improve your experience.',
    required: false,
  },
  {
    id: 'marketing',
    name: 'Marketing',
    description: 'Personalized advertising and content based on your interests.',
    required: false,
  },
];

export const consentConfig = {
  version: 1,
  storageKey: 'c15t-consent',
  expiryDays: 365,

  // UI text
  banner: {
    title: 'We Value Your Privacy',
    description: 'We use cookies to improve your experience on The Art of CTO. You can manage your preferences below.',
    privacyPolicyUrl: '/privacy',
    acceptAllLabel: 'Accept All',
    rejectAllLabel: 'Reject All',
    manageLabel: 'Manage Preferences',
    saveLabel: 'Save Preferences',
  },

  // Categories
  categories: consentCategories,

  // Backend API endpoints
  api: {
    save: '/api/public/consent',
    get: '/api/public/consent',
    update: '/api/public/consent',
  },
};

// Zaraz integration handlers
export function syncConsentToZaraz(acceptedCategories: string[]) {
  if (typeof window === 'undefined' || !window.zaraz) return;

  // Map our categories to Zaraz purposes
  const hasAnalytics = acceptedCategories.includes('analytics');
  const hasMarketing = acceptedCategories.includes('marketing');

  // Set Zaraz consent - must pass object with all purposes
  // NOTE: Purpose IDs must match exactly what's configured in Cloudflare Zaraz dashboard
  // Current Cloudflare purpose IDs: yzSe (Analytics), FqIY (Marketing)
  try {
    window.zaraz.consent.set({
      yzSe: hasAnalytics,    // Analytics purpose
      FqIY: hasMarketing,    // Marketing purpose
    });
    console.log('[Consent] Synced to Zaraz:', { hasAnalytics, hasMarketing });
  } catch (error) {
    console.warn('[Consent] Zaraz purposes not configured correctly. Check purpose IDs in Cloudflare dashboard.', error);
  }
}

// Type augmentation for window.zaraz
declare global {
  interface Window {
    zaraz?: {
      consent: {
        set: (purposes: Record<string, boolean>) => void;
        get: (purpose: string) => boolean | undefined;
      };
    };
  }
}
